import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AggregatedSlot, WeekDay } from '../../models/public-calendar.models';
import { PublicI18nService } from '../../services/public-i18n.service';
import { DEFAULT_TIMEZONE, TimezoneService } from '@frontend/util';
import { InfoTooltipComponent } from '@frontend/ui';

@Component({
  selector: 'pc-week-view',
  standalone: true,
  imports: [CommonModule, InfoTooltipComponent],
  styles: [`
    :host {
      display: block;
    }
    .day-card {
      border: 1px solid var(--color-border, #e2e8f0);
      border-radius: 12px;
      padding: 8px 2px;
      background: #fff;
      text-align: center;
      color: var(--color-foreground, #1e293b);
      transition: all 0.15s ease;
      cursor: pointer;
    }
    .day-card:hover:not(.day-card--selected):not(.day-card--disabled) {
      border-color: var(--color-primary, #0066ff);
    }
    .day-card--selected {
      border: 2px solid var(--color-primary, #0066ff);
      background: color-mix(in srgb, var(--color-primary, #0066ff) 10%, transparent);
    }
    .day-card--selected .day-card__number {
      color: var(--color-primary, #0066ff);
    }
    .day-card--today:not(.day-card--selected) {
      background: color-mix(in srgb, var(--color-primary, #0066ff) 6%, transparent);
    }
    .day-card--disabled {
      opacity: 0.35;
      border-color: transparent;
      background: #f9fafb;
      cursor: not-allowed;
    }
    .day-card__weekday {
      font-size: 10px;
      color: var(--color-muted, #94a3b8);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-weight: 500;
    }
    .day-card__number {
      font-size: 15px;
      font-weight: 600;
      line-height: 1.2;
    }
    .day-dots {
      display: flex;
      gap: 2px;
      justify-content: center;
      margin-top: 4px;
      min-height: 4px;
    }
    .day-dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: var(--color-muted, #94a3b8);
    }
    .day-card--selected .day-dot {
      background-color: var(--color-primary, #0066ff);
    }
    .slot-card {
      border: 1px solid var(--color-border, #e2e8f0);
      border-radius: 12px;
      padding: 14px 12px;
      background: #fff;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .slot-card:hover:not(.slot-card--selected) {
      border-color: var(--color-primary, #0066ff);
    }
    .slot-card--selected {
      border-color: var(--color-primary, #0066ff);
      background: color-mix(in srgb, var(--color-primary, #0066ff) 10%, transparent);
    }
    .slot-card__time {
      font-size: 15px;
      font-weight: 600;
      color: var(--color-foreground, #1e293b);
    }
    .slot-card__detail {
      font-size: 11px;
      color: var(--color-muted, #94a3b8);
      margin-top: 2px;
    }
    .text-theme-primary {
      color: var(--color-primary, #0066ff);
    }
    .spinner {
      border: 2px solid var(--color-border, #e2e8f0);
      border-top-color: var(--color-primary, #0066ff);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `],
  template: `
    <div class="rounded-xl border p-4 sm:p-6" style="border-color: var(--color-border, #e2e8f0); background-color: var(--color-surface, #f8fafc);">
      <!-- Navigation header -->
      <div class="flex items-center justify-between mb-5">
        <button
          type="button"
          class="p-2 -ml-2 rounded-lg transition-colors hover:bg-black/5"
          style="color: var(--color-foreground, #1e293b);"
          (click)="previousWeek()"
          aria-label="Previous week"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div class="text-center">
          <button
            type="button"
            class="text-sm text-theme-primary hover:underline font-medium"
            (click)="goToToday()"
          >
            {{ i18n.t('weekView.today') }}
          </button>
          <p class="font-semibold mt-0.5" style="color: var(--color-foreground, #1e293b);">{{ weekLabel() }}</p>
          <!-- Timezone indicator -->
          <div class="flex items-center justify-center gap-1 mt-1">
            <span class="text-xs opacity-60" style="color: var(--color-foreground, #1e293b);">
              🕐 {{ i18n.t('weekView.timesIn') }} {{ timezoneDisplayName() }}
            </span>
            <lib-info-tooltip size="sm" position="bottom-center">
              <div class="space-y-2">
                <p class="font-medium">{{ i18n.formatTimezoneShort(timezone()) }}</p>
                <p class="opacity-80">{{ i18n.t('weekView.timezoneExplanation') }}</p>
                @if (timezoneOffsetHours() !== 0) {
                  <p class="pt-1 border-t" style="border-color: var(--color-border, #e2e8f0);">
                    <span class="font-medium">{{ i18n.t('weekView.yourTime') }}:</span>
                    {{ viewerTimezoneDisplay() }}
                    <span class="opacity-80">
                      ({{ formatOffsetDisplay() }})
                    </span>
                  </p>
                } @else {
                  <p class="pt-1 border-t text-success" style="border-color: var(--color-border, #e2e8f0);">
                    ✓ {{ i18n.t('weekView.sameTimezone') }}
                  </p>
                }
              </div>
            </lib-info-tooltip>
          </div>
        </div>

        <button
          type="button"
          class="p-2 -mr-2 rounded-lg transition-colors hover:bg-black/5"
          style="color: var(--color-foreground, #1e293b);"
          (click)="nextWeek()"
          aria-label="Next week"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <!-- Day selector -->
      <div class="grid grid-cols-7 gap-1 sm:gap-2 mb-5">
        @for (day of weekDays(); track day.date) {
          <button
            type="button"
            class="day-card flex flex-col items-center justify-center min-h-[56px] sm:min-h-[64px]"
            [class.day-card--selected]="selectedDate() === day.date"
            [class.day-card--today]="day.isToday && selectedDate() !== day.date"
            [class.day-card--disabled]="isPastDate(day.date) && !hasAvailableFutureSlots(day.date)"
            [disabled]="isPastDate(day.date) && !hasAvailableFutureSlots(day.date)"
            (click)="selectDate(day.date)"
          >
            <span class="day-card__weekday">{{ day.dayName }}</span>
            <span class="day-card__number">{{ day.dayNumber }}</span>
            <div class="day-dots">
              @for (dot of getSlotDots(day.date); track $index) {
                <span class="day-dot"></span>
              }
            </div>
          </button>
        }
      </div>

      <!-- Slots for selected day -->
      @if (loading()) {
        <div class="flex justify-center py-12">
          <div class="spinner h-8 w-8"></div>
        </div>
      } @else {
        @if (futureSlotsForSelectedDay().length === 0) {
          <div class="text-center py-10">
            <div class="text-4xl mb-3">📅</div>
            <p class="opacity-60 mb-4" style="color: var(--color-foreground, #1e293b);">
              {{ i18n.t('weekView.noSlots') }}
            </p>
            @if (hasNextDayWithSlots()) {
              <button
                type="button"
                class="text-sm text-theme-primary hover:underline font-semibold"
                (click)="goToNextDayWithSlots()"
              >
                {{ i18n.t('weekView.showNextAvailable') }} →
              </button>
            }
          </div>
        } @else {
          <!-- Slot header -->
          <div class="flex items-center justify-between mb-3">
            <span class="font-semibold" style="color: var(--color-foreground, #1e293b);">
              {{ i18n.t('weekView.availableSlots') }}
            </span>
            <span class="text-sm" style="color: var(--color-muted, #94a3b8);">
              {{ i18n.t('weekView.slotsCount').replace('{count}', '' + futureSlotsForSelectedDay().length) }}
            </span>
          </div>
          <!-- Slot grid - 2 columns -->
          <div class="grid grid-cols-2 gap-2">
            @for (slot of futureSlotsForSelectedDay(); track slot.startTime) {
              <button
                type="button"
                class="slot-card text-left"
                [class.slot-card--selected]="isSlotSelected(slot)"
                (click)="onSlotSelect(slot)"
              >
                <div class="slot-card__time">{{ i18n.formatTime(slot.startTime) }}</div>
                <div class="slot-card__detail">{{ i18n.t('weekView.until') }} {{ i18n.formatTime(slot.endTime) }} · {{ getSlotDuration(slot) }} {{ i18n.t('weekView.minutes') }}</div>
              </button>
            }
          </div>
        }
      }
    </div>
  `,
})
export class WeekViewComponent {
  readonly i18n = inject(PublicI18nService);
  private readonly timezoneService = inject(TimezoneService);

  // Inputs
  slots = input<AggregatedSlot[]>([]);
  loading = input<boolean>(false);
  selectedSlot = input<AggregatedSlot | null>(null);
  /** Whether to filter out past time slots. Defaults to true. */
  filterPastSlots = input<boolean>(true);
  /** Timezone for date/time calculations. Should be provider's timezone from API response. */
  timezone = input<string>(DEFAULT_TIMEZONE);

  // Outputs
  slotSelected = output<AggregatedSlot>();
  weekChanged = output<{ from: string; to: string }>();

  // Internal state
  currentWeekStart = signal<Date>(this.getStartOfWeek(new Date()));
  selectedDate = signal<string>(this.getLocalDateString(new Date()));

  /** Viewer's local timezone detected from browser */
  viewerTimezone = computed(() => {
    if (typeof Intl !== 'undefined') {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return DEFAULT_TIMEZONE;
      }
    }
    return DEFAULT_TIMEZONE;
  });

  /** Provider timezone formatted for display (e.g., "Berlin (CET)") */
  timezoneDisplayName = computed(() => {
    return this.i18n.formatTimezoneShort(this.timezone());
  });

  /** Viewer timezone formatted for display */
  viewerTimezoneDisplay = computed(() => {
    return this.i18n.formatTimezoneShort(this.viewerTimezone());
  });

  /** Hour difference between provider and viewer timezone */
  timezoneOffsetHours = computed(() => {
    return this.i18n.getTimezoneOffsetHours(this.timezone(), this.viewerTimezone());
  });

  /** Format the offset display string (e.g., "+8 hours ahead" or "-3 hours behind") */
  formatOffsetDisplay(): string {
    const offset = this.timezoneOffsetHours();
    if (offset === 0) return '';

    const absOffset = Math.abs(offset);
    const sign = offset > 0 ? '+' : '-';

    if (offset > 0) {
      return `${sign}${absOffset} ${this.i18n.t('weekView.hoursAhead')}`;
    } else {
      return `${sign}${absOffset} ${this.i18n.t('weekView.hoursBehind')}`;
    }
  }

  // Computed values
  weekDays = computed<WeekDay[]>(() => {
    const days: WeekDay[] = [];
    const start = this.currentWeekStart();
    const tz = this.timezone();
    const today = this.timezoneService.toDateString(new Date(), tz);
    const dayNames = this.i18n.getDayNames();

    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      const dateStr = this.getLocalDateString(date);

      days.push({
        date: dateStr,
        dayName: dayNames[date.getDay()],
        dayNumber: date.getDate(),
        isToday: dateStr === today,
      });
    }

    return days;
  });

  weekLabel = computed(() => {
    const start = this.currentWeekStart();
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const dateLocale = this.i18n.getDateLocale();
    const startMonth = start.toLocaleDateString(dateLocale, { month: 'short' });
    const endMonth = end.toLocaleDateString(dateLocale, { month: 'short' });

    if (startMonth === endMonth) {
      return `${start.getDate()}. - ${end.getDate()}. ${endMonth} ${end.getFullYear()}`;
    }
    return `${start.getDate()}. ${startMonth} - ${end.getDate()}. ${endMonth} ${end.getFullYear()}`;
  });

  slotsForSelectedDay = computed(() => {
    return this.getSlotsForDay(this.selectedDate());
  });

  /** Future slots for the selected day (past slots filtered out) */
  futureSlotsForSelectedDay = computed(() => {
    return this.getFutureSlotsForDay(this.selectedDate());
  });

  getSlotsForDay(date: string): AggregatedSlot[] {
    return this.slots().filter((slot) => slot.date === date);
  }

  /** Get only future slots for a given day (filters out past slots if enabled) */
  getFutureSlotsForDay(date: string): AggregatedSlot[] {
    const daySlots = this.getSlotsForDay(date);
    if (!this.filterPastSlots()) {
      return daySlots;
    }
    const now = new Date();
    const tz = this.timezone();
    const nowHours = this.timezoneService.getHours(now, tz);
    const nowMinutes = this.timezoneService.getMinutes(now, tz);
    const nowTime = `${String(nowHours).padStart(2, '0')}:${String(nowMinutes).padStart(2, '0')}`;
    const todayStr = this.timezoneService.toDateString(now, tz);

    return daySlots.filter((slot) => {
      // Future dates - all slots are valid
      if (date > todayStr) return true;
      // Past dates - no slots valid
      if (date < todayStr) return false;
      // Today - only slots starting after current time
      return slot.startTime >= nowTime;
    });
  }

  hasAvailableSlots(date: string): boolean {
    return this.getSlotsForDay(date).length > 0;
  }

  /** Check if date has future slots available */
  hasAvailableFutureSlots(date: string): boolean {
    return this.getFutureSlotsForDay(date).length > 0;
  }

  /** Get count of available future slots for a date */
  getAvailableFutureSlotsCount(date: string): number {
    return this.getFutureSlotsForDay(date).length;
  }

  /** Check if a date is in the past */
  isPastDate(date: string): boolean {
    const today = this.timezoneService.toDateString(new Date(), this.timezone());
    return date < today;
  }

  /** Check if there's a day in the current week with available slots after the selected day */
  hasNextDayWithSlots(): boolean {
    const selectedIdx = this.weekDays().findIndex(d => d.date === this.selectedDate());
    return this.weekDays().slice(selectedIdx + 1).some(d => this.hasAvailableFutureSlots(d.date));
  }

  /** Navigate to the next day that has available slots */
  goToNextDayWithSlots(): void {
    const selectedIdx = this.weekDays().findIndex(d => d.date === this.selectedDate());
    const nextDay = this.weekDays().slice(selectedIdx + 1).find(d => this.hasAvailableFutureSlots(d.date));
    if (nextDay) {
      this.selectDate(nextDay.date);
    }
  }

  /** Get dot indicators for a day (max 5 dots based on slot count) */
  getSlotDots(date: string): number[] {
    const count = this.getFutureSlotsForDay(date).length;
    if (count === 0) return [];
    return Array.from({ length: Math.min(count, 5) });
  }

  /** Get slot duration in minutes */
  getSlotDuration(slot: AggregatedSlot): number {
    const [startH, startM] = slot.startTime.split(':').map(Number);
    const [endH, endM] = slot.endTime.split(':').map(Number);
    return (endH * 60 + endM) - (startH * 60 + startM);
  }

  isSlotSelected(slot: AggregatedSlot): boolean {
    const selected = this.selectedSlot();
    if (!selected) return false;
    return (
      selected.date === slot.date &&
      selected.startTime === slot.startTime &&
      selected.endTime === slot.endTime
    );
  }

  selectDate(date: string): void {
    this.selectedDate.set(date);
  }

  onSlotSelect(slot: AggregatedSlot): void {
    this.slotSelected.emit(slot);
  }

  previousWeek(): void {
    const newStart = new Date(this.currentWeekStart());
    newStart.setDate(newStart.getDate() - 7);
    this.currentWeekStart.set(newStart);
    this.emitWeekChange();
  }

  nextWeek(): void {
    const newStart = new Date(this.currentWeekStart());
    newStart.setDate(newStart.getDate() + 7);
    this.currentWeekStart.set(newStart);
    this.emitWeekChange();
  }

  goToToday(): void {
    const today = new Date();
    this.currentWeekStart.set(this.getStartOfWeek(today));
    this.selectedDate.set(this.getLocalDateString(today));
    this.emitWeekChange();
  }

  private emitWeekChange(): void {
    const from = this.getLocalDateString(this.currentWeekStart());
    const end = new Date(this.currentWeekStart());
    end.setDate(end.getDate() + 6);
    const to = this.getLocalDateString(end);

    this.weekChanged.emit({ from, to });
  }

  private getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private getLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Initialize week change on component init
  ngOnInit(): void {
    this.emitWeekChange();
  }
}
